import type { StaffAuthState } from '../../lib/auth';

// ─────────────────────────────────────────────────────────────────────
// WHAT A SIGNED-OUT VISITOR TO `/sa` IS SHOWN (#789).
//
// Where the deployment offers no dev staff door, `/sa` sends a signed-out
// visitor straight to Microsoft instead of rendering a page that advertises an
// internal control panel. Where it does (dev, `DEV_STAFF_LOGIN=enabled`), the
// two-door landing stays, because the dev door is the one that works there.
//
// WHICH DOORS EXIST IS NEVER AN ENVIRONMENT NAME. The Microsoft door exists
// when the bundle carries a valid Entra configuration (`StaffAuth.available`);
// the dev door exists when the SERVER's `/config` says its endpoint is
// mounted (`staffLogin.devFormEnabled`). The server refuses to mount it next
// to a production origin, so a manipulated UI cannot conjure it either.
//
// ── NO LOOP, BY CONSTRUCTION ──────────────────────────────────────
//
// An automatic redirect happens AT MOST ONCE PER TAB until somebody signs in.
// Everything that can bring the visitor back without a session — Entra
// answering an error, a cancel at Microsoft, the back button, a sign-in that
// worked but whose account `/me` refuses — lands on a neutral page with a
// button, never on a second automatic trip. The marker is in sessionStorage
// because the redirect leaves the page: an in-memory flag would not survive
// the very navigation it exists to stop repeating.
//
// A SIGNED-IN identity is never redirected, whatever it may or may not do.
// «Authenticated but no platform role» is an answer to render, and sending
// that person back to Microsoft would only return them to the same answer.
// ─────────────────────────────────────────────────────────────────────

export type SaEntry =
  /** Somebody is signed in: render the panel, which renders the server's answer. */
  | 'signed-in'
  /** Still asking: MSAL's cache, the cookie and `/config` have not all answered. */
  | 'checking'
  /** The deployment offers the dev door: the two-door landing. */
  | 'doors'
  /** Start the Entra sign-in now. */
  | 'redirect'
  /** The page is on its way to Microsoft. */
  | 'redirecting'
  /** An attempt was made and did not end signed in: say so, offer a retry. */
  | 'retry'
  /** No door at all here — Entra is off or misconfigured, and no dev door. */
  | 'unavailable';

export interface SaEntryInput {
  /** Whether the boot restores and the `/config` read have all settled. */
  booted: boolean;
  staff: StaffAuthState;
  clubSignedIn: boolean;
  /** `/config` says the dev staff door is mounted. */
  devDoor: boolean;
  /** This tab has already redirected once without a sign-in following. */
  attempted: boolean;
}

/** Pure, so every row of #789's table is a unit test rather than a render. */
export function saEntry(input: SaEntryInput): SaEntry {
  const { staff } = input;
  if (staff.status === 'signed-in' || input.clubSignedIn) return 'signed-in';
  // NOT BEFORE THE ANSWERS ARE IN. A configured Entra starts `signed-out`
  // before its cache has been read, so deciding now would redirect somebody
  // who is signed in — on every hard reload.
  if (!input.booted) return 'checking';
  if (input.devDoor) return 'doors';
  if (staff.status === 'disabled' || staff.status === 'misconfigured') return 'unavailable';
  if (staff.status === 'signing-in') return 'redirecting';
  if (staff.status === 'error' || input.attempted) return 'retry';
  return 'redirect';
}

const ATTEMPT_KEY = 'starpoint.sa.entraAutoRedirect';

// Every access guarded: storage can be absent or throw (a private window,
// blocked site data). Unreadable reads as «not attempted», which costs at
// most one redirect — and with storage blocked MSAL cannot redirect at all,
// so that one ends in the retry page, not a loop.

export function entraRedirectAttempted(): boolean {
  try {
    return window.sessionStorage.getItem(ATTEMPT_KEY) === '1';
  } catch {
    return false;
  }
}

export function markEntraRedirectAttempted(): void {
  try {
    window.sessionStorage.setItem(ATTEMPT_KEY, '1');
  } catch {
    // See above.
  }
}

export function clearEntraRedirectAttempted(): void {
  try {
    window.sessionStorage.removeItem(ATTEMPT_KEY);
  } catch {
    // See above.
  }
}
